import { cn } from '@/lib/utils';

/**
 * How far past a limit you are, or how far from it.
 *
 * IT APPEARS LATE, AND THE CALLER DECIDES HOW LATE. A counter watching every
 * keystroke is a different instruction from a limit you approach — the first says
 * *you are being measured*, the second says *you are near the end* — and the
 * limits in this product differ by two orders of magnitude (a bird's name against
 * `LETTER_MAX_CHARS`), so the threshold belongs to each caller and only the
 * rendering is shared. `countFrom` is the helper for the usual ratio.
 *
 * IT WAS `ComposeCounter`, LOCAL TO THE COMPOSER, until the profile form needed
 * one. The two things worth not duplicating are both invisible: `dir="ltr"`, which
 * is what keeps a minus sign on the correct side of the digits in RTL, and
 * `aria-live="polite"`, which is what makes the number reach somebody who cannot
 * see it. A second copy would have had neither, because neither is apparent from
 * looking at the first.
 */
export function CharCounter({
  remaining,
  show,
  over,
  className,
}: {
  remaining: number;
  show: boolean;
  over: boolean;
  className?: string;
}) {
  if (!show) return null;
  return (
    <span
      aria-live="polite"
      /* A signed number is not bidirectional text: in RTL the minus
         lands on the wrong side of the digits without this. */
      dir="ltr"
      className={cn(
        'text-[13px] tabular-nums',
        over ? 'text-destructive' : 'text-muted-foreground',
        className,
      )}
    >
      {remaining}
    </span>
  );
}

/**
 * The character count at which a counter is worth drawing: the last 30% of the
 * allowance.
 *
 * A RATIO RATHER THAN A CONSTANT, because "the last twenty characters" is most of
 * a bird's name and a rounding error in a letter. Every caller that has no reason
 * to differ should use this, so that approaching a limit feels the same wherever
 * it happens.
 */
export function countFrom(max: number): number {
  return Math.round(max * 0.7);
}
